// Mock data for the campaigns feature.
// Schema matches docs/crm-campaings/CAMPAIGN-TYPES-MODEL.md — channels + offer.
// Content is always wrapped in `content.variants[]` so A/B testing can ship as an additive change.

import { CAMPAIGN_STATUS, CHANNEL, OFFER, EMAIL_TEMPLATE } from './constants';

const variant = ({ subject, previewText = '', body, callToAction }) => ({
  id: 'default',
  subject,
  previewText,
  body,
  callToAction,
  audienceShare: 1.0,
});

const emptyMetrics = () => ({
  recipients: 0,
  delivered: 0,
  opened: 0,
  clicked: 0,
  converted: 0,
  revenue: 0,
  openRate: 0,
  clickRate: 0,
  ctr: 0,
  impressions: 0,
  uniqueImpressions: 0,
  bounces: 0,
  unsubscribes: 0,
});

export const MOCK_CAMPAIGNS = [
  {
    id: 'camp-001',
    name: 'Summer Sale 2026',
    description: 'Promotional email + in-app upsell with 30% off.',
    channels: [CHANNEL.EMAIL, CHANNEL.NETWORK_UPSELL],
    offer: OFFER.DISCOUNT,
    status: CAMPAIGN_STATUS.ACTIVE,
    template: EMAIL_TEMPLATE.PROMOTIONAL,
    audience: {
      filters: [
        { type: 'role', operator: 'in', value: ['reader'] },
        { type: 'hasPurchased', operator: 'eq', value: true },
        { type: 'purchaseAmount', operator: 'gte', value: 50 },
      ],
      estimatedReach: 2340,
      excludeUserIds: [],
    },
    content: {
      variants: [
        variant({
          subject: 'Summer Learning Sale — 30% Off!',
          previewText: 'Hand-picked courses, discounted for summer.',
          body: 'Get ready for summer with our exclusive course discounts…',
          callToAction: { text: 'Shop Now', url: 'https://school.coursio.com/store' },
        }),
      ],
      networkUpsell: {
        featuredCourseIds: ['course-marketing-101', 'course-graphic-design'],
      },
    },
    discount: {
      code: 'SUMMER30',
      percentage: 30,
      validUntil: '2026-05-01T23:59:59Z',
      scopeCourseIds: null, // null = all courses
    },
    schedule: {
      email: { sendAt: '2026-04-20T09:30:00Z', timezone: 'Europe/Stockholm', mode: 'scheduled' },
      networkUpsell: { activeFrom: '2026-04-20T09:30:00Z', activeUntil: '2026-05-04T09:30:00Z', timezone: 'Europe/Stockholm' },
      syncStartTimes: true,
    },
    mailchimp: { listId: 'mc-list-001', campaignId: 'mc-camp-5f23a1', synced: true, lastSyncAt: '2026-04-20T09:27:00Z' },
    metrics: {
      ...emptyMetrics(),
      recipients: 2340,
      delivered: 2297,
      opened: 658,
      clicked: 169,
      converted: 42,
      revenue: 3420,
      bounces: 43,
      openRate: 28.1,
      clickRate: 7.2,
      ctr: 7.2,
      impressions: 4120,
      uniqueImpressions: 1980,
    },
    createdBy: { id: 'user-001', name: 'Robert Bengtsson' },
    createdAt: '2026-04-18T14:00:00Z',
    updatedAt: '2026-04-20T09:30:00Z',
    deletedAt: null,
    anonymizedAt: null,
  },
  {
    id: 'camp-002',
    name: 'VIP Course Launch',
    description: 'Invitation to Marketing Pro for top students.',
    channels: [CHANNEL.EMAIL],
    offer: OFFER.INVITATION,
    status: CAMPAIGN_STATUS.COMPLETED,
    template: EMAIL_TEMPLATE.INVITATION,
    audience: {
      filters: [
        { type: 'role', operator: 'in', value: ['reader'] },
        { type: 'completedCourse', operator: 'in', value: ['course-marketing-basics'] },
      ],
      estimatedReach: 890,
      excludeUserIds: [],
    },
    content: {
      variants: [
        variant({
          subject: 'You earned free access to Marketing Pro!',
          previewText: 'A thank-you for finishing Marketing Basics.',
          body: 'Congratulations on completing Marketing Basics…',
          callToAction: { text: 'Accept invitation', url: 'https://school.coursio.com/gift' },
        }),
      ],
    },
    invitation: {
      courseId: 'course-marketing-pro',
      expiresAt: '2026-02-28T23:59:59Z',
    },
    schedule: {
      email: { sendAt: '2026-01-10T08:00:00Z', timezone: 'Europe/Stockholm', mode: 'scheduled' },
      syncStartTimes: false,
    },
    metrics: {
      ...emptyMetrics(),
      recipients: 890,
      delivered: 890,
      opened: 456,
      clicked: 234,
      converted: 156,
      openRate: 51.2,
      clickRate: 26.3,
      ctr: 26.3,
    },
    createdBy: { id: 'user-001', name: 'Robert Bengtsson' },
    createdAt: '2026-01-02T11:00:00Z',
    updatedAt: '2026-01-10T08:00:00Z',
    deletedAt: null,
    anonymizedAt: null,
  },
  {
    id: 'camp-003',
    name: 'Marketing 101 → Pro upsell',
    description: 'In-app recommendation card for Marketing 101 graduates.',
    channels: [CHANNEL.NETWORK_UPSELL],
    offer: OFFER.NONE,
    status: CAMPAIGN_STATUS.SCHEDULED,
    template: null,
    audience: {
      filters: [
        { type: 'role', operator: 'in', value: ['reader'] },
        { type: 'enrolledIn', operator: 'in', value: ['course-marketing-101'] },
        { type: 'notEnrolled', operator: 'in', value: ['course-marketing-pro'] },
      ],
      estimatedReach: 1120,
      excludeUserIds: [],
    },
    content: {
      variants: [variant({ subject: '', body: '', callToAction: null })],
      networkUpsell: {
        featuredCourseIds: ['course-marketing-pro'],
      },
    },
    schedule: {
      networkUpsell: { activeFrom: '2026-04-28T09:00:00Z', activeUntil: '2026-05-12T09:00:00Z', timezone: 'Europe/Stockholm' },
      syncStartTimes: false,
    },
    metrics: emptyMetrics(),
    createdBy: { id: 'user-001', name: 'Robert Bengtsson' },
    createdAt: '2026-04-15T10:00:00Z',
    updatedAt: '2026-04-21T16:00:00Z',
    deletedAt: null,
    anonymizedAt: null,
  },
  {
    id: 'camp-004',
    name: 'Spring Discount SPRING25',
    description: '25% off all courses for re-activation.',
    channels: [CHANNEL.EMAIL],
    offer: OFFER.DISCOUNT,
    status: CAMPAIGN_STATUS.PAUSED,
    template: EMAIL_TEMPLATE.DISCOUNT,
    audience: {
      filters: [{ type: 'role', operator: 'in', value: ['reader'] }],
      estimatedReach: 3400,
      excludeUserIds: [],
    },
    content: {
      variants: [
        variant({
          subject: 'Spring Special: 25% Off Everything',
          previewText: 'Use SPRING25 at checkout.',
          body: 'This weekend only, use code SPRING25…',
          callToAction: { text: 'Use Code', url: 'https://school.coursio.com/store' },
        }),
      ],
    },
    discount: {
      code: 'SPRING25',
      percentage: 25,
      validUntil: '2026-05-15T23:59:59Z',
      scopeCourseIds: null,
    },
    schedule: {
      email: { sendAt: '2026-04-14T09:00:00Z', timezone: 'Europe/Stockholm', mode: 'scheduled' },
      syncStartTimes: false,
    },
    metrics: {
      ...emptyMetrics(),
      recipients: 3400,
      delivered: 3320,
      opened: 1021,
      clicked: 326,
      converted: 98,
      revenue: 2940,
      openRate: 30.8,
      clickRate: 9.8,
      ctr: 9.8,
    },
    createdBy: { id: 'user-001', name: 'Robert Bengtsson' },
    createdAt: '2026-04-10T10:00:00Z',
    updatedAt: '2026-04-21T10:00:00Z',
    deletedAt: null,
    anonymizedAt: null,
  },
  {
    id: 'camp-005',
    name: 'Re-engagement — inactive 30d',
    description: 'Win back users inactive for 30+ days.',
    channels: [CHANNEL.EMAIL],
    offer: OFFER.NONE,
    status: CAMPAIGN_STATUS.DRAFT,
    template: EMAIL_TEMPLATE.REENGAGEMENT,
    audience: {
      filters: [
        { type: 'role', operator: 'in', value: ['reader'] },
        { type: 'lastActiveBefore', operator: 'lt', value: '2026-03-23' },
      ],
      estimatedReach: 620,
      excludeUserIds: [],
    },
    content: {
      variants: [
        variant({
          subject: 'We miss you! Come back for a look at what’s new',
          previewText: 'Fresh courses since you last visited.',
          body: 'It has been a while since we saw you…',
          callToAction: { text: 'See what’s new', url: 'https://school.coursio.com/store' },
        }),
      ],
    },
    schedule: {
      email: { sendAt: null, timezone: 'Europe/Stockholm', mode: 'scheduled' },
      syncStartTimes: false,
    },
    metrics: emptyMetrics(),
    createdBy: { id: 'user-001', name: 'Robert Bengtsson' },
    createdAt: '2026-04-20T10:00:00Z',
    updatedAt: '2026-04-20T10:00:00Z',
    deletedAt: null,
    anonymizedAt: null,
  },
];

export const MOCK_AUDIENCE_USERS = [
  { id: 'user-101', email: 'anna.johnson@example.com', name: 'Anna Johnson', role: 'reader', lastActive: '2026-04-18' },
  { id: 'user-102', email: 'erik.svensson@example.com', name: 'Erik Svensson', role: 'reader', lastActive: '2026-04-17' },
  { id: 'user-103', email: 'maria.garcia@company.com', name: 'Maria Garcia', role: 'reader', lastActive: '2026-04-16' },
  { id: 'user-104', email: 'john.smith@company.com', name: 'John Smith', role: 'reader', lastActive: '2026-04-15' },
  { id: 'user-105', email: 'lisa.anderson@example.com', name: 'Lisa Anderson', role: 'reader', lastActive: '2026-04-14' },
];

// Mock courses. Extra fields (coverUrl, description, price, currency) are
// here so the wizard's in-app preview can render the real CourseGridCard.
export const MOCK_COURSES_FOR_FILTER = [
  {
    id: 'course-marketing-101',
    name: 'Marketing 101',
    description: 'Foundations of digital marketing for new founders.',
    coverUrl: 'https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?w=600&q=70',
    price: 89,
    currency: 'USD',
    ownerName: 'Coursio',
  },
  {
    id: 'course-marketing-basics',
    name: 'Marketing Basics',
    description: 'A short primer on the four Ps of marketing.',
    coverUrl: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=600&q=70',
    price: 49,
    currency: 'USD',
    ownerName: 'Coursio',
  },
  {
    id: 'course-marketing-pro',
    name: 'Marketing Pro',
    description: 'Advanced tactics for ecommerce and SaaS growth.',
    coverUrl: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&q=70',
    price: 199,
    currency: 'USD',
    ownerName: 'Coursio',
  },
  {
    id: 'course-graphic-design',
    name: 'Graphic Design Fundamentals',
    description: 'Layout, typography and color for non-designers.',
    coverUrl: 'https://images.unsplash.com/photo-1561070791-2526d30994b8?w=600&q=70',
    price: 119,
    currency: 'USD',
    ownerName: 'Coursio',
  },
  {
    id: 'course-leadership',
    name: 'Leadership Essentials',
    description: 'Build trust, give feedback, run a team.',
    coverUrl: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&q=70',
    price: 149,
    currency: 'USD',
    ownerName: 'Coursio',
  },
  {
    id: 'course-sales',
    name: 'Sales Mastery',
    description: 'A full sales cycle from prospect to close.',
    coverUrl: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&q=70',
    price: 129,
    currency: 'USD',
    ownerName: 'Coursio',
  },
];

export const MOCK_GROUPS_FOR_FILTER = [
  { id: 'group-001', name: 'Enterprise Clients' },
  { id: 'group-002', name: 'Premium Members' },
  { id: 'group-003', name: 'Trial Users' },
];

export const CAMPAIGN_TEMPLATES = [
  { id: EMAIL_TEMPLATE.PROMOTIONAL, name: 'Promotional', description: 'Sales and discounts' },
  { id: EMAIL_TEMPLATE.INVITATION, name: 'Invitation', description: 'Course invitations' },
  { id: EMAIL_TEMPLATE.DISCOUNT, name: 'Discount code', description: 'Discount distribution' },
  { id: EMAIL_TEMPLATE.REENGAGEMENT, name: 'Re-engagement', description: 'Win back inactive users' },
  { id: EMAIL_TEMPLATE.RECOMMENDATION, name: 'Recommendation', description: 'Course upsells' },
  { id: EMAIL_TEMPLATE.ANNOUNCEMENT, name: 'Announcement', description: 'General updates' },
];

export const MOCK_CAMPAIGN_STATS = {
  activeCampaigns: 5,
  activeDelta: 2,
  totalSent: 12450,
  sentDelta: 18, // percent
  averageOpenRate: 24.3,
  openRateDelta: -1.2, // percentage points
  totalRevenue: 8420,
  revenueDelta: 1240,
};

export const MOCK_TOP_LINKS = [
  { url: 'school.coursio.com/sale', clicks: 112, unique: 96, percent: 66.3 },
  { url: 'school.coursio.com/marketing-pro', clicks: 38, unique: 34, percent: 22.5 },
  { url: 'school.coursio.com/unsubscribe', clicks: 19, unique: 19, percent: 11.2 },
];

export const MOCK_ACTIVITY_SERIES = [
  { tMinutes: 0, opens: 0, clicks: 0 },
  { tMinutes: 60, opens: 120, clicks: 30 },
  { tMinutes: 180, opens: 380, clicks: 96 },
  { tMinutes: 360, opens: 500, clicks: 130 },
  { tMinutes: 720, opens: 580, clicks: 150 },
  { tMinutes: 1440, opens: 630, clicks: 162 },
  { tMinutes: 2880, opens: 650, clicks: 167 },
  { tMinutes: 4320, opens: 658, clicks: 169 },
];
